import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Row, Spinner, Table } from 'react-bootstrap'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { LEAVE_STATUS, UPLOAD_BASE, fmtDateTH, fmtDateTimeTH, fmtDays, toBE } from './hrShared'

// การ์ดคำขอลา 1 รายการ สำหรับมุมมองมือถือ
const LeaveRequestCard = ({ r, onApprove, onReject }) => {
  const ls = LEAVE_STATUS[r.lr_status] || LEAVE_STATUS.pending
  return (
    <div className="border rounded p-3">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold text-truncate">{r.t_name}</div>
          <div className="fs-xs text-muted">{r.t_idnumber}</div>
        </div>
        <Badge bg={ls.bg}>{ls.label}</Badge>
      </div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div><span className="text-muted">ประเภท:</span> {r.lt_name}</div>
        <div><span className="text-muted">วันที่ลา:</span> {fmtDateTH(r.lr_start_date)} - {fmtDateTH(r.lr_end_date)} ({fmtDays(r.lr_days)} วัน)</div>
        {r.lr_reason && <div><span className="text-muted">เหตุผล:</span> {r.lr_reason}</div>}
        {r.lr_attachment && (
          <div>
            <a href={`${UPLOAD_BASE}/uploads/hr_leave/${r.lr_attachment}`} target="_blank" rel="noreferrer">
              <i className="ri-attachment-2 me-1" />ไฟล์แนบ
            </a>
          </div>
        )}
        {r.lr_status !== 'pending' && r.approve_note && (
          <div className="text-muted fs-xs mt-1">{r.approve_note}</div>
        )}
        <div className="text-muted fs-xs mt-1">ยื่นเมื่อ {fmtDateTimeTH(r.created_at)}</div>
      </div>
      {r.lr_status === 'pending' && (
        <div className="mt-2 pt-2 border-top d-flex gap-2">
          <Button size="sm" variant="success" className="flex-grow-1" onClick={onApprove}>
            <i className="ri-check-line" /> อนุมัติ
          </Button>
          <Button size="sm" variant="outline-danger" className="flex-grow-1" onClick={onReject}>
            <i className="ri-close-line" /> ไม่อนุมัติ
          </Button>
        </div>
      )}
    </div>
  )
}

export default function HrLeaveApproval() {
  const currentYear = new Date().getFullYear()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('pending')
  const [year, setYear] = useState(String(currentYear))
  const [keyword, setKeyword] = useState('')
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const fetchRequests = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      if (year) params.set('year', year)
      if (keyword) params.set('keyword', keyword)
      const res = await axiosInstance.get(`/api/hr/leave-requests?${params}`)
      if (res.data.success) setRequests(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [status, year, keyword])

  useEffect(() => {
    const timer = setTimeout(fetchRequests, 300)
    return () => clearTimeout(timer)
  }, [fetchRequests])

  const handleDecision = async (r, decision) => {
    const isApprove = decision === 'approved'
    const result = await Swal.fire({
      icon: isApprove ? 'question' : 'warning',
      title: `${isApprove ? 'อนุมัติ' : 'ไม่อนุมัติ'}คำขอลา?`,
      html: `<div class="text-start fs-6">
               <b>${r.t_name}</b><br/>
               ${r.lt_name} · ${fmtDateTH(r.lr_start_date)} - ${fmtDateTH(r.lr_end_date)} (${fmtDays(r.lr_days)} วัน)
             </div>`,
      input: 'textarea',
      inputPlaceholder: isApprove ? 'หมายเหตุ (ไม่บังคับ)' : 'เหตุผลที่ไม่อนุมัติ',
      showCancelButton: true,
      confirmButtonText: isApprove ? 'อนุมัติ' : 'ไม่อนุมัติ',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: isApprove ? '#198754' : '#dc3545',
      inputValidator: (value) => {
        if (!isApprove && !value) return 'กรุณาระบุเหตุผลที่ไม่อนุมัติ'
        return undefined
      },
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/hr/leave-requests/${r.lr_id}/status`, {
        status: decision,
        note: result.value || '',
      })
      Swal.fire({ icon: 'success', title: 'บันทึกผลแล้ว', timer: 1500, showConfirmButton: false })
      fetchRequests()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ดำเนินการไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const yearOptions = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2]

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-calendar-check-line me-2 text-primary" />
              อนุมัติการลา
            </h4>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one mb-3">
            <Card.Body className="p-3">
              <Row className="g-2">
                <Col xs={12} md={5}>
                  <InputGroup size="sm">
                    <InputGroup.Text><i className="ri-search-line" /></InputGroup.Text>
                    <Form.Control placeholder="ค้นหาชื่อหรือรหัสประจำตัว" value={keyword}
                      onChange={e => setKeyword(e.target.value)} />
                  </InputGroup>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Select size="sm" value={status} onChange={e => setStatus(e.target.value)}>
                    <option value="">สถานะทั้งหมด</option>
                    {Object.entries(LEAVE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </Form.Select>
                </Col>
                <Col xs={6} md={2}>
                  <Form.Select size="sm" value={year} onChange={e => setYear(e.target.value)}>
                    {yearOptions.map(y => <option key={y} value={y}>พ.ศ. {toBE(y)}</option>)}
                  </Form.Select>
                </Col>
                <Col xs={12} md={2}>
                  <Button size="sm" variant="outline-secondary" className="w-100"
                    onClick={() => { setKeyword(''); setStatus('pending'); setYear(String(currentYear)) }}>
                    <i className="ri-refresh-line me-1" />ล้างตัวกรอง
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center justify-content-between gap-2">
              <Card.Title as="h6" className="mb-0">รายการคำขอลา</Card.Title>
              <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setView('table')} title="มุมมองตาราง">
                  <i className="ri-table-line" />
                </button>
                <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setView('card')} title="มุมมองการ์ด">
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : requests.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่พบคำขอลา</div>
              ) : view === 'card' ? (
                <div className="p-3">
                  <Row className="g-3">
                    {requests.map(r => (
                      <Col key={r.lr_id} xs={12} md={6} xl={4}>
                        <LeaveRequestCard r={r}
                          onApprove={() => handleDecision(r, 'approved')}
                          onReject={() => handleDecision(r, 'rejected')} />
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th>ชื่อ-สกุล</th>
                      <th>ประเภท</th>
                      <th>วันที่ลา</th>
                      <th className="text-end">วัน</th>
                      <th>เหตุผล</th>
                      <th>ไฟล์แนบ</th>
                      <th>สถานะ</th>
                      <th>ยื่นเมื่อ</th>
                      <th style={{ width: 170 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map(r => {
                      const ls = LEAVE_STATUS[r.lr_status] || LEAVE_STATUS.pending
                      return (
                        <tr key={r.lr_id}>
                          <td>
                            {r.t_name}
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>{r.t_idnumber}</div>
                          </td>
                          <td>{r.lt_name}</td>
                          <td>{fmtDateTH(r.lr_start_date)} - {fmtDateTH(r.lr_end_date)}</td>
                          <td className="text-end">{fmtDays(r.lr_days)}</td>
                          <td className="text-truncate" style={{ maxWidth: 180 }} title={r.lr_reason || ''}>{r.lr_reason || '-'}</td>
                          <td>
                            {r.lr_attachment ? (
                              <a href={`${UPLOAD_BASE}/uploads/hr_leave/${r.lr_attachment}`} target="_blank" rel="noreferrer">
                                <i className="ri-attachment-2" />
                              </a>
                            ) : '-'}
                          </td>
                          <td>
                            <Badge bg={ls.bg}>{ls.label}</Badge>
                            {r.lr_status !== 'pending' && r.approve_note && (
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>{r.approve_note}</div>
                            )}
                          </td>
                          <td className="text-nowrap">{fmtDateTimeTH(r.created_at)}</td>
                          <td className="text-end">
                            {r.lr_status === 'pending' && (
                              <>
                                <Button size="sm" variant="success" className="me-1" onClick={() => handleDecision(r, 'approved')}>
                                  <i className="ri-check-line" /> อนุมัติ
                                </Button>
                                <Button size="sm" variant="outline-danger" onClick={() => handleDecision(r, 'rejected')}>
                                  <i className="ri-close-line" />
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />
    </>
  )
}
